/**
 * ProgramImageUpload Component
 * Handles program cover image upload with preview
 */

import React, { useState, useRef } from 'react';
import { FaCamera, FaTrash, FaSpinner } from 'react-icons/fa';
import { auth } from '../../../firebaseConfig';
import styles from './ProgramImageUpload.module.css';

const ProgramImageUpload = ({ value, onChange, clubId }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image');
      return;
    }

    if (file.size > maxSize) {
      setError('Image must be less than 5MB');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      // Get Firebase auth token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Please sign in to upload images');
      }

      const formData = new FormData();
      formData.append('programImage', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/programs/upload-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to upload image');
      }

      const data = await response.json();
      onChange(data.programImage);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
    setError(null);
  };

  const getImageUrl = () => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value.transformedUrls?.full || value.url;
  };

  const imageUrl = getImageUrl();

  return (
    <div className={styles.container}>
      <label className={styles.label}>Program Cover Image</label>
      
      <div className={`${styles.uploadArea} ${imageUrl ? styles.hasImage : ''}`}>
        {imageUrl ? (
          <>
            <img src={imageUrl} alt="Program cover" className={styles.preview} />
            <div className={styles.imageOverlay}>
              <button
                type="button"
                className={styles.changeBtn}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <FaCamera /> Change
              </button>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={handleRemove}
                disabled={isUploading}
              >
                <FaTrash /> Remove
              </button>
            </div>
          </>
        ) : (
          <div
            className={styles.placeholder}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            {isUploading ? (
              <>
                <FaSpinner className={styles.spinner} />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <FaCamera className={styles.icon} />
                <span className={styles.mainText}>Click to upload cover image</span>
                <span className={styles.subText}>Recommended: 1200 × 630 px</span>
                <span className={styles.formatText}>JPG, PNG, or WebP • Max 5MB</span>
              </>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className={styles.fileInput}
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};

export default ProgramImageUpload;









